import React, { useState, useEffect } from "react"
import { Modal, Checkbox, Button, Typography } from "antd"
import Strings from "utils/strings"

const { Text } = Typography

const formatMs = (ms: number) => {
	const h = Math.floor(ms / 3600000)
	const m = Math.floor((ms % 3600000) / 60000)
	return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
}

const formatRange = (r: { from: number; to: number }) =>
	`${formatMs(r.from)} - ${formatMs(r.to)}`

interface CloserOptimizerWarningModalProps {
	visible: boolean
	mismatchedOrders: any[]
	totalOrders: number
	onConfirm: (selectedIds: string[]) => void
	onCancel: () => void
}

const CloserOptimizerWarningModal: React.FC<
	CloserOptimizerWarningModalProps
> = ({ visible, mismatchedOrders, totalOrders, onConfirm, onCancel }) => {
	const [selected, setSelected] = useState<Set<string>>(new Set())

	useEffect(() => {
		if (visible) {
			setSelected(new Set())
		}
	}, [visible, mismatchedOrders])

	const toggle = (id: string) => {
		setSelected((prev) => {
			const next = new Set(prev)
			if (next.has(id)) next.delete(id)
			else next.add(id)
			return next
		})
	}

	const shift =
		mismatchedOrders.length > 0 ? mismatchedOrders[0].orderFlow?.shift : null

	const matchingCount = totalOrders - mismatchedOrders.length
	const sendCount = matchingCount + selected.size

	return (
		<Modal
			visible={visible}
			title={Strings.orders.closerWarningTitle}
			onCancel={onCancel}
			footer={[
				<Button key="cancel" onClick={onCancel}>
					{Strings.generic.cancel}
				</Button>,
				<Button
					key="confirm"
					type="primary"
					onClick={() => onConfirm(Array.from(selected))}
				>
					{Strings.orders.closerWarningSend.replace(
						"{0}",
						sendCount.toString()
					)}
				</Button>,
			]}
			width={520}
		>
			<div style={{ marginBottom: 12 }}>
				<Text>{Strings.orders.closerWarningDescription}</Text>
			</div>

			{shift && (
				<div style={{ marginBottom: 16 }}>
					<Text strong>{Strings.orders.shift}: </Text>
					<Text code>{formatRange(shift)}</Text>
				</div>
			)}

			<div style={{ marginBottom: 8, display: "flex", gap: 8 }}>
				<Button
					size="small"
					onClick={() =>
						setSelected(new Set(mismatchedOrders.map((o) => o._id)))
					}
				>
					{Strings.routes.selectAll}
				</Button>
				<Button size="small" onClick={() => setSelected(new Set())}>
					{Strings.routes.selectNone}
				</Button>
			</div>

			<div
				style={{
					maxHeight: 320,
					overflowY: "auto",
					border: "1px solid #f0f0f0",
					borderRadius: 6,
				}}
			>
				{mismatchedOrders.map((order, i) => {
					const slot = order.slot
					const orderId = order.orderId || order._id
					return (
						<div
							key={order._id}
							style={{
								display: "flex",
								alignItems: "center",
								gap: 10,
								padding: "8px 12px",
								borderBottom:
									i < mismatchedOrders.length - 1
										? "1px solid #f0f0f0"
										: "none",
								background: selected.has(order._id) ? "#f6ffed" : undefined,
							}}
						>
							<Checkbox
								checked={selected.has(order._id)}
								onChange={() => toggle(order._id)}
							/>
							<div style={{ flex: 1, minWidth: 0 }}>
								<Text strong style={{ fontSize: 13 }}>
									{orderId}
								</Text>
							</div>
							<div
								style={{
									display: "flex",
									flexDirection: "column",
									alignItems: "flex-end",
									gap: 2,
									fontSize: 12,
								}}
							>
								<span>
									<Text type="secondary">{Strings.orders.slot}: </Text>
									<Text code style={{ fontSize: 11 }}>
										{slot ? formatRange(slot) : "—"}
									</Text>
								</span>
							</div>
						</div>
					)
				})}
			</div>
		</Modal>
	)
}

export { CloserOptimizerWarningModal }
